import { useCallback, useMemo, useState } from "react";
import { noop, partition } from "lodash";
import { useApolloClient } from "@apollo/client";

import Graph_QL, { DynamicDataObk, GqlHook_Returns, StateDataObject } from "..";
import { useAppDispatch, useAppSelector } from "../../../Store";
import { filterByConnection } from "../filterByConnection";
import { PanelBasicQueryInfoInput } from "../FeedDataCalls/GQLCalls/getFeedComponentsCall";
import { gqlActions } from "../gqlData_Reducer";

import { LanControllerItem } from "./LocationFeedingSlices/lanControllerSlice";
import { GET_LAN_CONTROLLERS, LanControllerInfo, LanControllersResponse } from "./GQLCalls/getLanControllersCall";
import { CreatePanelController, MAINTAIN_CONTROLLER_INFO, MaintainControllerInfoParameters, MaintainControllerResponse } from "./GQLCalls/maintainControllerInfo";
import { GET_LAN_CONTROLLER_LOGS, LanControllerLogArgs, LanControllerLogParameters, LanControllerLogResponse } from "./GQLCalls/getLanControllerLogs";
import { GET_LAN_CONTROLLER_REGISTERS, LanControllerRegistersResponse } from "./GQLCalls/getLanControllerRegistersCall";

interface LanControllerElements {
  getLanControllerLogs: (input: Omit<LanControllerLogParameters,"domain">, specific?: Graph_QL.GQLCons) => any;
}

export const useLanControllers = (connection: Graph_QL.GQLCons): GqlHook_Returns<LanControllerItem, PanelBasicQueryInfoInput> & LanControllerElements => {
  const dispatch = useAppDispatch();
  const client = useApolloClient();
  const [, setCursorId] = useState(-1);

  const lanControllersState = useAppSelector(({graphQlData:{domain}}) => domain.lanControllers);
  const {fullState} = useMemo(() => {
    const state = filterByConnection(lanControllersState, Graph_QL.ToArr(connection)) as Record<string, LanControllerItem>
    return Object.entries(state).reduce((accumulator, [key, data]) => {
      if (data.data) {
        const value = {...data.data, domain:key}; 
        accumulator.data = accumulator.data.concat(value);
      }
      if (data.cpuOptions) {
        const value = {...data.cpuOptions, domain: key};
        accumulator.cpuOptions = accumulator.cpuOptions.concat(value);
      }
      return accumulator
    },{fullState: state, data: [], cpuOptions: [] } as StateDataObject<LanControllerItem>)
  },[connection, lanControllersState]);

  const getLanControllers = async (input: PanelBasicQueryInfoInput, specific?: Graph_QL.GQLCons) => {
    const usedConnection = specific?.connection ? specific.connection : connection.connection;
    try {
      client.query<LanControllersResponse>({query: GET_LAN_CONTROLLERS, variables: {...usedConnection, input}})
        .then((e) => {
          if (e && e.data) {
            dispatch({type: String(gqlActions.domain.lanControllers.maintained), payload: {con: connection?.connection, data: e.data.controller.info}});
            setCursorId(e.data.controller.info.at(-1)?.controller_id ?? -1)
          }
        }).catch((error) => console.log(error));
    } catch (error) {
      console.log(error);
    }
  };

  const getCpuOptions = useCallback(async (specific?: Graph_QL.GQLCons, offset?: number) => {
    const usedConnection = specific?.connection ? specific.connection : connection.connection;
    try {
      client.query<LanControllerRegistersResponse>({query: GET_LAN_CONTROLLER_REGISTERS, variables: {...usedConnection, limit: 50, offset: offset ? offset : 0}})
        .then((response) => {
          if (response && response.data) {
            dispatch({type: String(gqlActions.domain.lanControllers.maintained), payload: {con: usedConnection, data: [], cpuOptions: response.data.controller.lanControllers}})
          }
        })
    } catch (error) {
      console.log(error);
    }
  },[client, connection.connection, dispatch])

  const getLanControllerLogs = useCallback(async (input: Omit<LanControllerLogParameters,"domain">, specific?: Graph_QL.GQLCons) => {
    let logData = {};
    var Connections: Graph_QL.MultiConnection | undefined = Graph_QL.ToArr(specific ? specific : connection)
    if (Connections) {
      logData = await Promise.all(Connections.cons.map(async (_Connection) => {
        var data = await client.query<LanControllerLogResponse, LanControllerLogParameters>({ query: GET_LAN_CONTROLLER_LOGS, variables: { domain: _Connection.connection.domain, ...input } })
        return {con:_Connection.connection, data:data.data.controller.logs}
      })).then(e => {
        dispatch({ type: '' + gqlActions.device.log.added, payload: e })
        return {con:null, data:[]}
      })
    }
    return logData
  },[client, connection, dispatch])

  const maintainControllerInfo = async (changedData?: Partial<DynamicDataObk<Partial<LanControllerInfo>[]>>) => {
    const newControllerInfo = changedData?.data ? changedData.data[0] : undefined;

    if (!newControllerInfo) {
      return;
    }
    const [newControllers , updatedControllers] = partition(changedData?.data, (controllerInfo) => (!Boolean(controllerInfo.controller_id))) as [CreatePanelController[], LanControllerInfo[]];
    try {
      // TODO REMAP VALUES SO THEY FIT GRAPHQL
      client.mutate<MaintainControllerResponse, MaintainControllerInfoParameters>({
        mutation: MAINTAIN_CONTROLLER_INFO,
        variables: {
          ...connection.connection,
          newControllers: newControllers.map(({cpu_id, ...remaining}) => {
            return {...remaining, cpu_id}
          }),
          updatedControllers: updatedControllers.map(({controller_id, cpu_id, ...remaining}) => {
            return {...remaining, cpu_id, controller_id}

          })
        }})
        .then((result) => {
          if (result && result.data) {
            dispatch({type: String(gqlActions.domain.lanControllers.maintained), payload: {con: connection?.connection, data: result.data.controller.maintainInfo}});
          }
        }
      ).catch((error) => {console.log(error)})
    } catch {
      console.warn("The update failed -> No changes dispatched");
    }
  } 

  return {call: getLanControllers, getCpuOptions, getLanControllerLogs, fetchMore: noop, deleteEntries: noop, data: fullState, maintainEntries: maintainControllerInfo}
} 

